import {Injectable} from "@angular/core";
import {APIService} from "./api.service";
import {BehaviorSubject, map, Observable, of} from "rxjs";
import {IPlayer} from "../interfaces/player/IPlayer";
import {IPlayerSearchParams} from "../interfaces/player/IPlayerSearchParams";
import {IPlayerSearchResult} from "../interfaces/player/IPlayerSearchResult";
import {IPlayerPerGameQueryResult} from "../interfaces/player/IPlayerPerGameQueryResult";
import {IApiResponseBase} from "../interfaces/IApiResponseBase";
import {IDeactivatePlayerNumbers} from "../interfaces/player/IDeactivatePlayerNumbers";
import {IReactivatePlayerNumbers} from "../interfaces/player/IReactivatePlayerNumbers";
import {IPlayerPaymentTransactions} from "../interfaces/player/IPlayerPaymentTransactions";
import {HttpParams} from "@angular/common/http";
import {IDeactivatePlayer} from "../interfaces/player/IDeactivatePlayer";
import {IReactivatePlayer} from "../interfaces/player/IReactivatePlayer";
import {IAdminUpdatePlayerBasicInfo} from "../interfaces/player/IAdminUpdatePlayerBasicInfo";
import {createStore} from "@ngneat/elf";
import {getActiveEntity, setEntities, withActiveId, withEntities} from "@ngneat/elf-entities";
import {tap} from "rxjs/operators";
import {ISuspendPlayer} from "../interfaces/player/ISuspendPlayer";
import {IPlayerSuspensionQueryResult} from "../interfaces/player/IPlayerSuspensionQueryResult";
import {IDialogGenericData} from "../interfaces/IDialogGenericData";
import {
  ConfirmationActionDialogComponent
} from "../components/dialogs/confirmation-action-dialog/confirmation-action-dialog.component";
import {DialogFunctionService} from "./dialog-function.service";
import {MatDialog} from "@angular/material/dialog";
import {IGeneralPlayerInfo} from "../interfaces/player/IGeneralPlayerInfo";
import {IPlayerCharityPledge} from "../interfaces/player/IPlayerCharityPledge";
import {IPlayerUnsubscribeResult} from "../interfaces/player/IPlayerUnsubscribeResult";
import {IRemoveUnsubscribesForPlayer} from "../interfaces/player/IRemoveUnsubscribesForPlayer";
import {ILastFiveFailedEmailsQueryResult} from "../interfaces/player/ILastFiveFailedEmailsQueryResult";
import {IPlayerEmail} from "../interfaces/player/IPlayerEmail";
import {IGoldRushTicketStripQueryResult} from "../interfaces/player/IGoldRushTicketStripQueryResult";
import {IPlayerWinningsQueryResult} from "../interfaces/IPlayerWinningsQueryResult";
import {IPlayerEmailFilterParams} from "../interfaces/player/IPlayerEmailFilterParams";
import {
  IFiftyFiftyPlayerNumbersForPlayerInGameInstanceQueryResult
} from "../interfaces/number/IFiftyFiftyPlayerNumbersForPlayerInGameInstanceQueryResult";
import {IPaymentRefund} from "../interfaces/player/IPaymentRefund";
import {IPlayerRefundsQueryResult} from "../interfaces/player/IPlayerRefundsQueryResult";
import {IUpdatePlayerSuspensionStatus} from "../interfaces/player/IUpdatePlayerSuspensionStatus";
import { IAutoplayGroupWithPaymentTier } from "../interfaces/IAutoplayGroupWithPaymentTier";
import {IPlayerPaymentMethodQueryResult} from "../interfaces/player/IPlayerPaymentMethodQueryResult";
import {IPlayerPaymentMethodFilter} from "../interfaces/player/IPlayerPaymentMethodFilter";
import {IDuplicateCardFingerprintResult} from "../interfaces/player/IDuplicateCardFingerprintResult";
import {IDuplicateCardFingerprintGroup} from "../interfaces/player/IDuplicateCardFingerprintGroup";
import {IDuplicateCardFingerprintGroupPlayer} from "../interfaces/player/IDuplicateCardFingerprintGroupPlayer";

@Injectable({
  providedIn: 'root'
})
export class PlayersService {

  private playerStore = createStore(
    {name: "player-state"},
    withEntities<IPlayerPerGameQueryResult, "Id">(),
    withActiveId()
  );

  public fetchUpdatedTicketNumbersSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public fetchUpdatedTicketNumbersSubject$: Observable<boolean> = this.fetchUpdatedTicketNumbersSubject.asObservable();

  public fetchUpdatedSuspensionsSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  constructor(private apiService: APIService,
              private matDialog: MatDialog,
              private dialogFunctionService: DialogFunctionService) {
  }

  // deprecated and is going away
  public getPlayerGameInfo(playerIdP: string, gameIdP: string): Observable<IPlayerPerGameQueryResult> {
    return this.apiService.MakeGetRequest<IPlayerPerGameQueryResult>(`player/${playerIdP}/for-game/${gameIdP}`)
      .pipe(tap(res => this.playerStore.update(setEntities([res]))));
  }

  public getGeneralPlayerData(playerIdP: string, adminIdP: string): Observable<IGeneralPlayerInfo> {
    return this.apiService.MakeGetRequest<IGeneralPlayerInfo>(`player/general-info/${playerIdP}/${adminIdP}`);
  }

  public updateBasicPlayerInfo(commandP: IAdminUpdatePlayerBasicInfo): Observable<IApiResponseBase> {
    return this.apiService.MakePutRequest<IApiResponseBase>(`player/admin-update/basic-info`, commandP);
  }

  public getFiftyFiftyPlayerNumbersByGameInstance(playerIdP: string, gameInstanceIdP: string): Observable<IFiftyFiftyPlayerNumbersForPlayerInGameInstanceQueryResult> {
    return this.apiService.MakeGetRequest<IFiftyFiftyPlayerNumbersForPlayerInGameInstanceQueryResult>(`player-number/by-player/${playerIdP}/for-fifty-fifty-game-instance/${gameInstanceIdP}`);
  }

  public getGoldRushStripsByPlayerIdAndGameId(playerIdP: string, gameIdP: string): Observable<IGoldRushTicketStripQueryResult> {
    return this.apiService.MakeGetRequest<IGoldRushTicketStripQueryResult>(`player-number/by-player/${playerIdP}/for-gold-rush-game/${gameIdP}`);
  }

  public getPlayerCharityPledgesByGameId(playerIdP: string, gameIdP: string): Observable<IPlayerCharityPledge[]> {
    return this.apiService.MakeGetRequest<IPlayerCharityPledge[]>(`player/pledges/${playerIdP}/${gameIdP}`);
  }

  public getPlayerPaymentMethods(playerIdP: string, gameIdP: string): Observable<IPlayerPaymentMethodQueryResult[]> {
    return this.apiService.MakeGetRequest<IPlayerPaymentMethodQueryResult[]>(`player-payment-method/${playerIdP}/game/${gameIdP}`);
  }

  public getPaymentMethodsByGame(filterP: IPlayerPaymentMethodFilter): Observable<IPlayerPaymentMethodQueryResult[]> {
    let params = new HttpParams();
    params = params.append('GameId', filterP.gameId);

    if (filterP.playerId) {
      params = params.append('PlayerId', filterP.playerId);
    }
    if (filterP.paymentMethodId) {
      params = params.append('PaymentMethodId', filterP.paymentMethodId);
    }
    if (filterP.cardFingerprint) {
      params = params.append('CardFingerprint', filterP.cardFingerprint);
    }
    if (filterP.cardLastFourDigits) {
      params = params.append('CardLastFourDigits', filterP.cardLastFourDigits);
    }
    if (filterP.playerEmail) {
      params = params.append('PlayerEmail', filterP.playerEmail);
    }

    return this.apiService.MakeGetRequest<IPlayerPaymentMethodQueryResult[]>(`player-payment-method/by-game`, params);
  }

  public getOtherPlayersWithSameCardFingerprints(playerIdP: string, gameIdP: string): Observable<IDuplicateCardFingerprintResult[]> {
    const params = new HttpParams().set('gameId', gameIdP);

    return this.apiService.MakeGetRequest<IDuplicateCardFingerprintResult[]>(`player-payment-method/duplicate-fingerprints/${playerIdP}`, params);
  }

  public getDuplicateCardFingerprintsForGame(gameIdP: string): Observable<IDuplicateCardFingerprintGroup[]> {
    let params = new HttpParams();
    params = params.append('GameId', gameIdP);

    return this.apiService.MakeGetRequest<IDuplicateCardFingerprintGroup[]>(`player-payment-method/duplicate-fingerprints/by-game`, params);
  }

  public getPlayersForDuplicateCardFingerprint(gameIdP: string, fingerprintP: string): Observable<IDuplicateCardFingerprintGroupPlayer[]> {
    let params = new HttpParams();
    params = params.append('GameId', gameIdP);
    params = params.append('fingerprint', fingerprintP);

    return this.apiService.MakeGetRequest<IDuplicateCardFingerprintGroupPlayer[]>(`player-payment-method/duplicate-fingerprints/by-game/players`, params);
  }

  public getPlayerTransactionsByGameId(playerIdP: string, gameIdP: string): Observable<IPlayerPaymentTransactions[]> {
    return this.apiService.MakeGetRequest<{
      Transactions: IPlayerPaymentTransactions[]
    }>(`player/transactions/${playerIdP}/${gameIdP}`)
      .pipe(map((res) => res.Transactions));
  }

  public getPlayerRefundsByGameId(playerIdP: string, gameIdP: string): Observable<IPaymentRefund[]> {
    return this.apiService.MakeGetRequest<IPlayerRefundsQueryResult>(`player/refunds/${playerIdP}/${gameIdP}`)
      .pipe(map((res) => res.Refunds));
  }

  public deactivatePlayerNumbersByGroup(deactivateCommandP: IDeactivatePlayerNumbers): Observable<IApiResponseBase> {
    return this.apiService.MakePutRequest<IApiResponseBase>(`player-number/deactivate-numbers`, deactivateCommandP);
  }

  public deactivatePlayer(deactivatePlayerCommandP: IDeactivatePlayer): Observable<IApiResponseBase> {
    return this.apiService.MakePutRequest<IApiResponseBase>(`player/deactivate`, deactivatePlayerCommandP);
  }

  public onDeactivatePlayerClick(playerInfo: IPlayerPerGameQueryResult) {
    this.dialogFunctionService.setCallback(() => {
      return this.deactivatePlayer({
        playerId: playerInfo?.Id!
      })
    });

    if (playerInfo) {
      let deactivatePlayerDialogData: IDialogGenericData = {
        title: 'Confirm Player Deactivation',
        message: `Please confirm that you would like to deactivate the following player`,
        playerData: {
          firstName: playerInfo.FirstName,
          email: playerInfo.Email,
          lastName: playerInfo.LastName
        },
        dataCyTag: 'confirm-deactivate-player-button',
        successMessage: 'Successfully deactivated player',
        extraDetails: 'Deactivating a player will remove all of their numbers from all games, except any numbers already played for for any active games. The user will no longer have access to any games.'
      };

      const dialogRef = this.matDialog.open(ConfirmationActionDialogComponent, {
        data: deactivatePlayerDialogData,
        width: '650px'
      });

      dialogRef.afterClosed().subscribe(
        {
          next: (updateSuccessful: boolean) => {
            if (updateSuccessful) {
            }
            return of(null);
          }
        })
    }
  }

  public reactivatePlayer(reactivatePlayerCommandP: IReactivatePlayer): Observable<IApiResponseBase> {
    return this.apiService.MakePutRequest<IApiResponseBase>(`player/reactivate`, reactivatePlayerCommandP);
  }

  public activatePlayerNumbers(reactivateCommandP: IReactivatePlayerNumbers): Observable<IApiResponseBase> {
    return this.apiService.MakePutRequest<IApiResponseBase>(`player-number/reactivate-numbers`, reactivateCommandP);
  }

  public getPlayersBySearchParams(paramsP?: IPlayerSearchParams, isGlobalSearch: boolean = false) {
    let queryParams: HttpParams = new HttpParams();

    if (paramsP?.gameId) {
      queryParams = queryParams.append('gameId', paramsP?.gameId)
    }

    if (paramsP?.firstName) {
      queryParams = queryParams.append('firstName', paramsP?.firstName)
    }

    if (paramsP?.lastName) {
      queryParams = queryParams.append('lastName', paramsP?.lastName)
    }

    if (paramsP?.email) {
      queryParams = queryParams.append('email', paramsP?.email)
    }

    if (paramsP?.playerId) {
      queryParams = queryParams.append('playerId', paramsP?.playerId)
    }

    if (paramsP?.phoneNumber) {
      queryParams = queryParams.append('phoneNumber', paramsP?.phoneNumber)
    }

    if (paramsP?.maskData) {
      queryParams = queryParams.append('maskData', paramsP?.maskData)
    }

    let apiUrl = isGlobalSearch ? 'player/global-search' : 'player/search';

    // TotalMatches comes back alongside the rows because the search is capped server-side: the caller has to
    // be able to say "500 of 3,214" rather than presenting a trimmed list as the whole answer.
    return this.apiService.MakeGetRequest<IPlayerSearchResult>(apiUrl, queryParams);
  }

  public suspendPlayer(suspendPlayerP: ISuspendPlayer) {
    return this.apiService.MakePostRequest<ISuspendPlayer>("player/suspend", suspendPlayerP);
  }

  public updatePlayerSuspensionStatus(updateSuspensionP: IUpdatePlayerSuspensionStatus) {
    return this.apiService.MakePostRequest<IUpdatePlayerSuspensionStatus>("player/update-suspension-status", updateSuspensionP);
  }

  public getPlayerSuspensionsForGame(playerIdP: string, gameIdP: string) {
    return this.apiService.MakeGetRequest<IPlayerSuspensionQueryResult>(`player/suspensions/all-for-player/${playerIdP}/in-game/${gameIdP}`);
  }

  public getUnsubscribesForPlayer(emailP: string, gameIdP?: string) {
    return this.apiService.MakeGetRequest<IPlayerUnsubscribeResult>(`player/unsubscribes/for-player/${emailP}/${gameIdP}`);
  }

  public deleteUnsubscribesForPlayer(commandP: IRemoveUnsubscribesForPlayer) {
    return this.apiService.MakePostRequest("player/remove-unsubscribes-for-player", commandP);
  }

  public getLastFiveFailedEmailsForPlayer(emailP: string, gameIdP?: string) {
    return this.apiService.MakeGetRequest<ILastFiveFailedEmailsQueryResult>(`player/last-five-failed-emails/for-player/${emailP}/${gameIdP}`);
  }

  public getActivePlayer() {
    return this.playerStore.query(getActiveEntity());
  }

  public getAllPlayerSuspensions(playerIdP: string) {
    return this.apiService.MakeGetRequest<IPlayerSuspensionQueryResult>(`player/suspensions/all-for-player/${playerIdP}`);
  }

  public getAllEmailsForPlayer(playerIdP: string) {
    return this.apiService.MakeGetRequest<IPlayerEmail[]>(`player/all-emails-for-player/${playerIdP}`);
  }

  public getWinnerResultsForPlayerInGame(playerIdP: string, gameIdP: string) {
    return this.apiService.MakeGetRequest<IPlayerWinningsQueryResult>(`player/winner-results-for-player/${playerIdP}/in-gold-rush-game/${gameIdP}`);
  }

  public getEmailChangeByFilterParams(paramsP: IPlayerEmailFilterParams) {
    let queryParams: HttpParams = new HttpParams();

    if (paramsP) {
      if (paramsP.Email) {
        queryParams = queryParams.append('email', paramsP.Email);
      }

      if (paramsP.PlayerId) {
        queryParams = queryParams.append('playerId', paramsP.PlayerId);
      }
    }

    return this.apiService.MakeGetRequest<IPlayerEmail[]>("player/email-changes", queryParams);
  }

  public stripPhoneFormatting(phoneNumber: string | null): string {
    return phoneNumber ? phoneNumber.replace(/[-\s\(\)]/g, '') : '';
  }

  public getAutoplayGroupsForPlayerInGame(playerIdP: string, gameIdP: string): Observable<IAutoplayGroupWithPaymentTier[]> {
    return this.apiService.MakeGetRequest<IAutoplayGroupWithPaymentTier[]>(`autoplay-group/player/${playerIdP}/game/${gameIdP}/with-payment-tiers`);
  }

  public unlockPlayerAccount(unlockPlayerCommandP: { playerId: string; gameId: string }): Observable<IApiResponseBase> {
    return this.apiService.MakePutRequest<IApiResponseBase>(`player/unlock-account`, unlockPlayerCommandP);
  }
}
